/**
 * Registro de comidas (Req. 18).
 *
 * - Cada formulario crea el id al abrirse y se guarda con `put`: tocar dos
 *   veces "Guardar" no duplica (Req. 1.9).
 * - Se valida antes de tocar la base (Req. 18.11) y el instante (`momento`)
 *   siempre sale de la fecha y la hora capturadas, para el ayuno (Req. 18.7).
 * - Cada registro lleva su copia de valores: lo que pase después con el
 *   alimento no cambia lo registrado (Req. 18.4).
 */

import { db } from './schema';
import { enEscritura } from './tx';
import { nuevoId } from './ids';
import type { Day, MealEntry, TiempoComida } from './types';
import { ahoraIso, esFechaLocal, sumarDias, type FechaLocal } from '@/domain/dates';
import { esHoraLocal, instanteLocal, type HoraLocal } from '@/domain/time';
import { validarRegistro } from '@/domain/nutricion';
import { ErrorValidacion } from '@/domain/errors';
import { cerrarAyunoAbiertoAlComer } from './ayunos';

export interface DiaDeComida {
  fecha: FechaLocal;
  entradas: MealEntry[];
  /** Req. 18.9: se excluye del mantenimiento estimado y del ayuno calculado. */
  registroIncompleto: boolean;
}

/** LECTURA PURA, apta para useLiveQuery. */
export async function leerDiaDeComida(fecha: FechaLocal): Promise<DiaDeComida> {
  const [entradas, dia] = await Promise.all([
    db.mealEntries.where('fecha').equals(fecha).toArray(),
    db.days.get(fecha),
  ]);
  return { fecha, entradas, registroIncompleto: dia?.registroIncompleto ?? false };
}

export async function leerComida(id: string): Promise<MealEntry | undefined> {
  return db.mealEntries.get(id);
}

/** Las comidas de un periodo, para recientes y frecuencia (Req. 19.6). */
export async function leerComidasEntre(desde: FechaLocal, hasta: FechaLocal): Promise<MealEntry[]> {
  return db.mealEntries.where('fecha').between(desde, hasta, true, true).toArray();
}

/** Las de los últimos 60 días, contando hoy. */
export async function leerComidasRecientes(hoy: FechaLocal): Promise<MealEntry[]> {
  return leerComidasEntre(sumarDias(hoy, -59), hoy);
}

export function validarComida(e: MealEntry): void {
  if (!esFechaLocal(e.fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (!esHoraLocal(e.hora)) throw new ErrorValidacion('La hora no es válida.', 'hora');
  const error = validarRegistro(e);
  if (error !== null) throw new ErrorValidacion(error);
}

/** Guarda un registro nuevo o editado. ESCRIBE. */
export async function guardarComida(entrada: MealEntry): Promise<MealEntry> {
  validarComida(entrada);
  const registro: MealEntry = {
    ...entrada,
    nombre: entrada.nombre.trim(),
    nota: entrada.nota === null || entrada.nota.trim() === '' ? null : entrada.nota.trim(),
    momento: instanteLocal(entrada.fecha, entrada.hora),
    actualizadoEn: ahoraIso(),
  };
  await enEscritura([db.mealEntries, db.fasts], async () => {
    await db.mealEntries.put(registro);
    // Registrar un alimento que rompe el ayuno termina el que estuviera
    // corriendo, con la hora de CONSUMO (Req. 23.14). Desde aquí empieza la
    // ventana de alimentación, que no es ayuno
    if (registro.rompeAyuno) await cerrarAyunoAbiertoAlComer(registro.momento);
  });
  return registro;
}

export async function borrarComida(id: string): Promise<void> {
  await enEscritura([db.mealEntries], async () => {
    await db.mealEntries.delete(id);
  });
}

/**
 * Una copia igual, en el mismo día, tiempo y hora (Req. 18.6). La copia ya no
 * cuenta como el registro de una comida habitual (0.3.5): es aparte.
 */
export async function duplicarComida(id: string, idCopia: string = nuevoId('com')): Promise<MealEntry> {
  return enEscritura([db.mealEntries], async () => {
    const original = await db.mealEntries.get(id);
    if (original === undefined) throw new ErrorValidacion('Ese registro ya no existe.');
    const ahora = ahoraIso();
    const copia: MealEntry = {
      ...original,
      ...(original.habitual === undefined || original.habitual === null ? {} : { habitual: null }),
      id: idCopia,
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await db.mealEntries.put(copia);
    return copia;
  });
}

export interface CopiaDeTiempo {
  /** El día de donde se copia. */
  desde: FechaLocal;
  /** El día a donde se copia. */
  hacia: FechaLocal;
  tiempo: TiempoComida;
  /**
   * Los registros de ese día y tiempo que se copian, en el orden en que deben
   * quedar, con el id de cada copia (la pantalla lo crea una sola vez).
   */
  copias: ReadonlyArray<{ origenId: string; id: string }>;
  /**
   * La hora de lo primero que se copia en el día de destino (Req. 18.7). Lo
   * demás conserva la diferencia que tenía ese día: el postre de las 21:30
   * sigue hora y media después de la cena de las 20:00.
   */
  hora: HoraLocal;
}

const minutosDe = (h: HoraLocal) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
const horaDe = (minutos: number): HoraLocal => {
  // Sin pasar al día siguiente ni al anterior: la fecha y el instante quedan en ese día
  const m = Math.min(23 * 60 + 59, Math.max(0, minutos));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/**
 * Copia registros de un tiempo de comida de otro día (Req. 18.6) con sus
 * mismas cantidades y valores (la copia que guardó cada uno, Req. 18.4). Lo
 * primero queda a la hora indicada y lo demás con la misma diferencia de ese
 * día, porque la hora es la del ayuno. Se agregan a lo que ya hay ese día.
 * Las copias no cuentan como una comida habitual (0.3.5): esa se confirma
 * aparte. Con los ids de la pantalla, tocar dos veces no duplica. ESCRIBE.
 */
export async function copiarTiempoDeComida(c: CopiaDeTiempo): Promise<MealEntry[]> {
  if (!esFechaLocal(c.desde) || !esFechaLocal(c.hacia)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (c.desde === c.hacia) throw new ErrorValidacion('Elige otro día: ese es el mismo.');
  if (!esHoraLocal(c.hora)) throw new ErrorValidacion('La hora no es válida.', 'hora');
  if (c.copias.length === 0) throw new ErrorValidacion('Marca al menos un registro para copiar.');
  return enEscritura([db.mealEntries], async () => {
    const delDia = await db.mealEntries.where('fecha').equals(c.desde).toArray();
    const originales = c.copias.map(({ origenId }) => {
      const original = delDia.find((e) => e.id === origenId && e.tiempo === c.tiempo);
      if (original === undefined) throw new ErrorValidacion('Ese día cambió: vuelve a abrir esta pantalla.');
      return original;
    });
    const desplazamiento = minutosDe(c.hora) - Math.min(...originales.map((o) => minutosDe(o.hora)));
    const base = Date.now();
    const copias = originales.map((original, i) => {
      const hora = horaDe(minutosDe(original.hora) + desplazamiento);
      // Un milisegundo de diferencia: a la misma hora, quedan en el orden de ese día
      const marca = new Date(base + i).toISOString();
      const copia: MealEntry = {
        ...original,
        ...(original.habitual === undefined || original.habitual === null ? {} : { habitual: null }),
        id: c.copias[i]?.id ?? nuevoId('com'),
        fecha: c.hacia,
        hora,
        momento: instanteLocal(c.hacia, hora),
        creadoEn: marca,
        actualizadoEn: marca,
      };
      validarComida(copia);
      return copia;
    });
    await db.mealEntries.bulkPut(copias);
    return copias;
  });
}

/** Marca o desmarca un día como "registro incompleto" (Req. 18.9). */
export async function marcarRegistroIncompleto(fecha: FechaLocal, incompleto: boolean): Promise<void> {
  if (!esFechaLocal(fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  await enEscritura([db.days], async () => {
    const dia = await db.days.get(fecha);
    const nuevo: Day = {
      fecha,
      registroIncompleto: incompleto,
      nota: dia?.nota ?? null,
      actualizadoEn: ahoraIso(),
    };
    await db.days.put(nuevo);
  });
}
